/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IOrderFavorite } from '../order-favorites/get-order-favorites.action';
import { ErrorCode } from '../../modules/order-details';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { escapeODataStringLiteral } from '../../utilities/odata';

/**
 * GetOrderFavorite Input Action
 * Looks up whether a single order (identified by the transactionId query parameter of the
 * order-details page) is marked as a favorite, instead of fetching the full favorites list.
 */
export class GetOrderFavoriteInput implements Msdyn365.IActionInput {
    public orderNumber: string;

    constructor(ctx: Msdyn365.ICreateActionContext) {
        this.orderNumber = ctx.requestContext.query?.transactionId || '';
    }

    public getCacheKey = () => `GetOrderFavorite-${this.orderNumber}`;
    public getCacheObjectType = () => 'GetOrderFavorite';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IGetOrderFavoriteData {
    '@odata.context'?: string;
    Success: boolean;
    Favorites: IOrderFavorite[];
    error?: ErrorCode;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetOrderFavoriteInput(args);
};

export async function action(input: GetOrderFavoriteInput, ctx: Msdyn365.IActionContext): Promise<IGetOrderFavoriteData> {
    ensureAuthenticated(ctx);

    if (!input.orderNumber) {
        return { Success: true, Favorites: [] };
    }

    const response = await Msdyn365.sendRequest<IGetOrderFavoriteData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/ExistsInFavorites(orderNumber='${escapeODataStringLiteral(input.orderNumber)}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );

    if (response.status === 200 && response.data) {
        return { ...response.data };
    }

    return { Success: false, Favorites: [], error: { errorCode: response.status, errorText: response.statusText } };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetOrderFavoriteData>>action,
    id: 'GetOrderFavorite',
    input: createInput
});
